import type { MarketingCta, MarketingLink } from "@/components/marketing";
import {
  Award,
  CalendarHeart,
  ChartColumn,
  ClipboardList,
  Dumbbell,
  Flame,
  Flower2,
  Handshake,
  KeyRound,
  ListChecks,
  MessageCircle,
  Package,
  PackageOpen,
  QrCode,
  RefreshCw,
  Repeat,
  RotateCcw,
  Settings2,
  ShoppingBag,
  ShoppingBasket,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  UserPlus,
  Users,
} from "lucide-react";
import type { IndustryPageData } from "../components";

// The nine industry pages that share one Website 2.0 template (the
// preview's t-loyalty-program-for-spas, -gyms, -yoga-studios,
// -fitness-studios, -ecommerce, -b2b-ecommerce, -manufacturing,
// -consumer-brands and -enterprise). Copy is ported as-is; each page's
// sections are listed in the preview's order and drawn by <IndustryPage>.
//
// Links point only at live routes:
// - "Milestones" (#/tiered-loyalty-program/milestones) goes to
//   /tiered-loyalty-program#milestones.
// - "#/products--x" and "#/solutions--x" go to /products/x and
//   /solutions/x.
// - Journey step icons are drawn from lucide-react (the preview's are
//   inline SVGs of the same ideas).

// Copy every page in the family shares.
export const industryPageShared = {
  // The goal tracker's count line ("3 of 6 sessions").
  goalCount: "{done} of {goal} {unit}",
};

const heroCtas = [
  { label: "Book a Demo", route: "/partner-signup" },
  { label: "Contact Us", route: "/contact", variant: "brandOutline" },
] satisfies MarketingCta[];

const finalCtaLinks = {
  cta: { label: "Book a Demo", route: "/partner-signup" },
  secondary: { label: "Contact Us", route: "/contact" },
} satisfies { cta: MarketingLink; secondary: MarketingLink };

const spas = {
  seo: {
    path: "/loyalty-program-for-spas",
    title: "Loyalty Program for Spas and Wellness Centres | Fydo",
    description: "Reward spa guests for treatments and packages with session rewards, package reminders and warm messages that bring them back to unwind.",
  },
  breadcrumb: "Spa",
  accent: "mint",
  hero: {
    title: "Make every treatment the start of a routine",
    lead: "Spa guests come to unwind, and the best ones come back on a rhythm. Reward packages and repeat treatments, and give guests a gentle reason to book the next visit.",
    ctas: heroCtas,
    scene: "spa",
    tone: "mint",
    alt: "Spa illustration",
    chips: [
      {
        text: "Package **4 of 6**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Your next treatment is waiting 🌿",
        variant: "whatsapp",
        x: 42,
        y: 93,
      },
      {
        text: "Free add-on unlocked",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "Where spas lose guests",
        intro: "A lovely visit does not guarantee a return one.",
      },
      cards: [
        {
          title: "Visits feel like one-off treats",
          text: "Without a reason to return, guests treat the spa as an occasional indulgence.",
        },
        {
          title: "Packages go unused",
          text: "Guests buy a package and drift away before they finish it.",
        },
        {
          title: "Quiet slots stay empty",
          text: "Weekday mornings and off-peak hours fill slowly.",
        },
        {
          title: "Gifts and occasions are missed",
          text: "Birthdays and anniversaries are natural moments to invite a guest back.",
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try a treatment package",
        intro: "Add sessions and watch the package reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 6,
        unit: "sessions",
        title: "A 6 session wellness package",
        addLabel: "Add a session",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "A free aromatherapy add-on",
          note: "Offered on the sixth session.",
        },
      },
    },
    {
      kind: "mechanisms",
      head: {
        tone: "white",
        id: "mechanisms",
        heading: "What Fydo can do for a spa",
        intro: "Rewards for care that lasts.",
      },
      cards: [
        {
          title: "Package progress",
          text: "Show guests how far through a package they are and what they earn at the end.",
          ui: {
            kind: "stamps",
            filled: 3,
            total: 5,
            note: "3 of 5 visits",
            title: "Free dessert next",
          },
        },
        {
          title: "Treatment rewards",
          text: "A free add-on or upgrade after a number of treatments.",
          ui: {
            kind: "ticket",
            label: "Reward code",
            code: "FYD-4K9",
            note: "Show it at the counter",
          },
        },
        {
          title: "Quiet-hour offers",
          text: "Offers for the slots you want to fill.",
          ui: {
            kind: "clock",
            label: "Quiet hours",
            value: "4 to 6 PM",
            tag: "Special offer",
          },
        },
        {
          title: "Occasion messages",
          text: "A birthday or anniversary treat, sent at the right time.",
          ui: {
            kind: "calendar",
            message: "Happy anniversary 💍",
          },
        },
        {
          title: "Aftercare messages",
          text: "A thank-you and a care tip after a treatment.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Recognise regulars",
          text: "Single out guests who visit most often.",
          ui: {
            kind: "vip",
            title: "VIP",
            note: "Early access message",
          },
        },
      ],
    },
    {
      kind: "journey",
      head: {
        tone: "cream",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "A guest's routine",
          steps: [
            {
              icon: Sparkles,
              title: "First treatment",
              text: "A guest enjoys a first visit and joins the programme.",
            },
            {
              icon: ListChecks,
              title: "Package and progress",
              text: "They see their progress and what the next session earns.",
            },
            {
              icon: CalendarHeart,
              title: "A reason to return",
              text: "An occasion message or a reminder brings them back.",
            },
          ],
        },
      ],
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Keep the calm going until the next booking.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Day Spa",
        subtitle: "Business account",
      },
      message: {
        text: "Hi Anjali, thank you for visiting Fydo Day Spa. Enjoy a free aromatherapy add-on on your next treatment this month.",
        card: {
          title: "Free add-on",
          note: "on your next visit",
        },
        button: "Book a treatment",
        time: "10:42 am",
      },
      panel: {
        title: "A message after the treatment",
        text: "Keep the calm going until the next booking.",
        points: [
          "Sent after a visit, with a care tip",
          "Carries a reward for the next treatment",
          "Timed for quiet hours when you need bookings",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "How is this different from a salon programme?",
          answer: "Spas run on packages and rituals, so rewards focus on package progress and the next treatment, not a short rebooking cycle.",
        },
        {
          question: "Can I reward a package?",
          answer: "Yes. You can set a reward for completing a number of sessions.",
        },
        {
          question: "Can I fill quiet hours?",
          answer: "Yes. Send offers for chosen days and times to the guests you pick.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Salons and beauty",
          route: "/loyalty-software-for-salon",
          description: "Keep clients on a steady rebooking rhythm.",
        },
        {
          label: "Milestones",
          route: "/tiered-loyalty-program#milestones",
          description: "Rewards when customers reach goals you define.",
        },
        {
          label: "WhatsApp Marketing",
          route: "/whatsapp-loyalty-marketing",
          description: "Campaigns, offers and notifications on WhatsApp.",
        },
        {
          label: "Gyms",
          route: "/loyalty-program-for-gyms",
          description: "Support consistency and renewals.",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about Spa",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const gyms = {
  seo: {
    path: "/loyalty-program-for-gyms",
    title: "Loyalty Program for Gyms | Fydo",
    description: "Reward gym members for showing up with attendance streaks, milestones, renewal rewards and friend referrals that keep memberships active.",
  },
  breadcrumb: "Gyms",
  accent: "brand",
  hero: {
    title: "Reward the members who keep showing up",
    lead: "The hardest thing in a gym is consistency. Recognise streaks and milestones, nudge members when attendance dips, and give them a reason to renew.",
    ctas: heroCtas,
    scene: "gym",
    tone: "blush",
    alt: "Gyms illustration",
    chips: [
      {
        text: "**12 day** streak 🔥",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "We missed you this week!",
        variant: "whatsapp",
        x: 48,
        y: 93,
      },
      {
        text: "Renewal reward",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try an attendance streak",
        intro: "Add visits and watch the monthly reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 12,
        unit: "visits",
        title: "Visit 12 times this month",
        addLabel: "Add a visit",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "A free personal training session",
          note: "Unlocked when the monthly target is reached.",
        },
      },
    },
    {
      kind: "challenge",
      head: {
        tone: "tint",
        id: "challenge",
        heading: "Where gyms lose members",
        intro: "Most people start strong. Few keep going.",
      },
      cards: [
        {
          title: "Motivation fades after the first weeks",
          text: "Many members slow down once the early excitement is gone.",
        },
        {
          title: "Renewals are the real test",
          text: "A lapsed habit quickly becomes a lapsed membership.",
        },
        {
          title: "Quiet members go unnoticed",
          text: "Without a signal, a member can stop coming for weeks before anyone notices.",
        },
        {
          title: "Joining is easy, staying is not",
          text: "The first month decides whether a member becomes a regular.",
        },
      ],
    },
    {
      kind: "mechanisms",
      head: {
        tone: "white",
        id: "mechanisms",
        heading: "What Fydo can do for a gym",
        intro: "Rewards that follow the member's habit.",
      },
      cards: [
        {
          title: "Attendance streaks",
          text: "A reward for a number of visits in a month.",
          ui: {
            kind: "stamps",
            filled: 3,
            total: 5,
            note: "3 of 5 visits",
            title: "Free dessert next",
          },
        },
        {
          title: "Milestone rewards",
          text: "Recognise the 50th visit and the first year.",
          ui: {
            kind: "goal",
            label: "Monthly goal",
            value: "₹7,200",
            of: "of ₹10,000",
            progress: 72,
            note: "₹2,800 to go",
          },
        },
        {
          title: "Refer-a-friend",
          text: "Training with a friend helps both stay.",
          ui: {
            kind: "segments",
            items: [
              {
                label: "New",
                segment: "new",
              },
              {
                label: "Repeat",
                segment: "repeat",
              },
              {
                label: "VIP",
                segment: "vip",
              },
              {
                label: "At risk",
                segment: "atRisk",
              },
              {
                label: "Lost",
                segment: "lost",
              },
            ],
          },
        },
        {
          title: "Renewal nudges",
          text: "A reminder and a reward before a membership ends.",
          ui: {
            kind: "clock",
            label: "Quiet hours",
            value: "4 to 6 PM",
            tag: "Special offer",
          },
        },
        {
          title: "Quiet-week messages",
          text: "A friendly message when attendance dips.",
          ui: {
            kind: "bubble",
            message: "We miss you! Here is something for your next visit.",
            time: "11:12 am ✓✓",
          },
        },
        {
          title: "Top member recognition",
          text: "Single out your most consistent members.",
          ui: {
            kind: "vip",
            title: "VIP",
            note: "Early access message",
          },
        },
      ],
    },
    {
      kind: "journey",
      head: {
        tone: "cream",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "A member's year",
          steps: [
            {
              icon: UserPlus,
              title: "Join and settle in",
              text: "A new member earns a reward for completing the first month.",
            },
            {
              icon: Flame,
              title: "Build the habit",
              text: "Streaks and milestones recognise every step.",
            },
            {
              icon: RefreshCw,
              title: "Renew",
              text: "A nudge and a reward before the membership ends keeps them going.",
            },
          ],
        },
      ],
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Bring members back before the habit breaks.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Gym",
        subtitle: "Business account",
      },
      message: {
        text: "Hi Rahul, you have not visited this week. Visit three times next week and earn a free personal training session.",
        card: {
          title: "Free session",
          note: "for your next 3 visits",
        },
        button: "See my progress",
        time: "10:42 am",
      },
      panel: {
        title: "A nudge when attendance dips",
        text: "Bring members back before the habit breaks.",
        points: [
          "Sent after a quiet week",
          "Sets a small goal with a reward",
          "Keeps the gym top of mind",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "How does a gym reward attendance?",
          answer: "You set a goal, such as a number of visits in a month, and the member earns a reward when they reach it.",
        },
        {
          question: "Can rewards be tied to renewals?",
          answer: "Yes. You can send a renewal reminder with a reward before a membership ends.",
        },
        {
          question: "Can members refer friends?",
          answer: "Yes. You can reward members who bring a friend to train.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Yoga Studios",
          route: "/loyalty-program-for-yoga-studios",
          description: "Reward practice, class packs and community.",
        },
        {
          label: "Fitness Studios",
          route: "/loyalty-program-for-fitness-studios",
          description: "Reward challenges, milestones and referrals.",
        },
        {
          label: "Milestones",
          route: "/tiered-loyalty-program#milestones",
          description: "Rewards when customers reach goals you define.",
        },
        {
          label: "Referral rewards",
          route: "/referral-rewards-software",
          description: "Reward customers who bring their friends.",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about Gyms",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const yogaStudios = {
  seo: {
    path: "/loyalty-program-for-yoga-studios",
    title: "Loyalty Program for Yoga Studios | Fydo",
    description: "Reward yoga students for steady practice with class pack progress, practice streaks, workshop invitations and warm messages from your studio.",
  },
  breadcrumb: "Yoga Studios",
  accent: "lilac",
  hero: {
    title: "Support a steady practice, one class at a time",
    lead: "Yoga is a habit built slowly. Reward regular practice, help students finish their class packs, and keep your community close with messages that feel personal.",
    ctas: heroCtas,
    scene: "yoga",
    tone: "mint",
    alt: "Yoga Studios illustration",
    chips: [
      {
        text: "Class pack **7 of 10**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "See you at the 7 am class 🧘",
        variant: "whatsapp",
        x: 44,
        y: 93,
      },
      {
        text: "Practice streak",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "What yoga studios face",
        intro: "A loyal community is built class by class.",
      },
      cards: [
        {
          title: "Class packs expire unused",
          text: "Students buy a pack and let it lapse before using every class.",
        },
        {
          title: "Beginners drop out early",
          text: "Many new students stop after a few classes without ever becoming regulars.",
        },
        {
          title: "Timetables have quiet slots",
          text: "Some classes fill up while others stay half empty.",
        },
        {
          title: "Community needs a thread",
          text: "Workshops, retreats and events need a way to reach the right students.",
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try a class pack",
        intro: "Add classes and watch the pack reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 10,
        unit: "classes",
        title: "A 10 class pack",
        addLabel: "Attend a class",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "A free workshop seat",
          note: "Offered when the pack is completed.",
        },
      },
    },
    {
      kind: "journey",
      head: {
        tone: "white",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "A student's practice",
          steps: [
            {
              icon: Flower2,
              title: "First classes",
              text: "A new student joins and earns a welcome reward after a few classes.",
            },
            {
              icon: TrendingUp,
              title: "Build the practice",
              text: "Class packs and milestones recognise steady attendance.",
            },
            {
              icon: Users,
              title: "Join the community",
              text: "Invitations to workshops and retreats keep them close.",
            },
          ],
        },
      ],
    },
    {
      kind: "mechanisms",
      head: {
        tone: "cream",
        id: "mechanisms",
        heading: "What Fydo can do for a yoga studio",
        intro: "Rewards that respect the pace of practice.",
      },
      cards: [
        {
          title: "Class pack progress",
          text: "Show students how many classes they have used and what finishing earns.",
          ui: {
            kind: "stamps",
            filled: 3,
            total: 5,
            note: "3 of 5 visits",
            title: "Free dessert next",
          },
        },
        {
          title: "Practice milestones",
          text: "Recognise the 25th class and the first year of practice.",
          ui: {
            kind: "goal",
            label: "Monthly goal",
            value: "₹7,200",
            of: "of ₹10,000",
            progress: 72,
            note: "₹2,800 to go",
          },
        },
        {
          title: "Welcome for beginners",
          text: "A reward for completing the first few classes.",
          ui: {
            kind: "ticket",
            label: "Reward code",
            code: "FYD-4K9",
            note: "Show it at the counter",
          },
        },
        {
          title: "Quiet-class offers",
          text: "An offer for the slots you want to fill.",
          ui: {
            kind: "clock",
            label: "Quiet hours",
            value: "4 to 6 PM",
            tag: "Special offer",
          },
        },
        {
          title: "Workshop invitations",
          text: "Invite regular students to workshops and retreats.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Bring a friend",
          text: "Reward students who bring a friend to class.",
          ui: {
            kind: "segments",
            items: [
              {
                label: "New",
                segment: "new",
              },
              {
                label: "Repeat",
                segment: "repeat",
              },
              {
                label: "VIP",
                segment: "vip",
              },
              {
                label: "At risk",
                segment: "atRisk",
              },
              {
                label: "Lost",
                segment: "lost",
              },
            ],
          },
        },
      ],
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Help students finish what they started.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Yoga Studio",
        subtitle: "Business account",
      },
      message: {
        text: "Namaste Divya, you have 3 classes left in your pack. Finish it by month end and join our Sunday workshop free.",
        card: {
          title: "Free workshop seat",
          note: "for finishing your class pack",
        },
        button: "See my classes",
        time: "10:42 am",
      },
      panel: {
        title: "A gentle reminder",
        text: "Help students finish what they started.",
        points: [
          "Sent when a pack is nearly used",
          "Offers a reward for finishing",
          "Keeps the tone warm and personal",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "Can I reward class packs?",
          answer: "Yes. You can set a reward when a student completes a pack or reaches a number of classes.",
        },
        {
          question: "Can I invite students to workshops?",
          answer: "Yes. Send WhatsApp invitations to regular students or any group you choose.",
        },
        {
          question: "Is it right for a small studio?",
          answer: "Yes. You can start with one simple reward and add more as your community grows.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Gyms",
          route: "/loyalty-program-for-gyms",
          description: "Support consistency and renewals.",
        },
        {
          label: "Fitness Studios",
          route: "/loyalty-program-for-fitness-studios",
          description: "Reward challenges, milestones and referrals.",
        },
        {
          label: "Milestones",
          route: "/tiered-loyalty-program#milestones",
          description: "Rewards when customers reach goals you define.",
        },
        {
          label: "WhatsApp Marketing",
          route: "/whatsapp-loyalty-marketing",
          description: "Campaigns, offers and notifications on WhatsApp.",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about Yoga Studios",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const fitnessStudios = {
  seo: {
    path: "/loyalty-program-for-fitness-studios",
    title: "Loyalty Program for Fitness Studios | Fydo",
    description: "Turn trial sessions into members and keep them training with challenges, session milestones, referral rewards and timely messages for your studio.",
  },
  breadcrumb: "Fitness Studios",
  accent: "sky",
  hero: {
    title: "Turn trial sessions into long-term members",
    lead: "Boutique studios live on small groups, strong coaching and word of mouth. Reward challenges and milestones, welcome new members well, and let your regulars bring their friends.",
    ctas: heroCtas,
    scene: "fitness",
    tone: "sky",
    alt: "Fitness Studios illustration",
    chips: [
      {
        text: "Milestone: **50th session**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Your challenge ends Sunday 💪",
        variant: "whatsapp",
        x: 44,
        y: 93,
      },
      {
        text: "Friend joined: reward",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "What fitness studios deal with",
        intro: "Small classes mean every member counts.",
      },
      cards: [
        {
          title: "Trial sessions rarely convert",
          text: "Many people try a class once and never join.",
        },
        {
          title: "Early weeks decide everything",
          text: "New members who do not form a routine in the first month tend to leave.",
        },
        {
          title: "Members drift away quietly",
          text: "Cost, time and motivation all pull members out, often without a word.",
        },
        {
          title: "Growth depends on referrals",
          text: "Small studios grow when happy members bring friends.",
        },
      ],
    },
    {
      kind: "mechanisms",
      head: {
        tone: "tint",
        id: "mechanisms",
        heading: "What Fydo can do for a studio",
        intro: "Rewards built around challenges and community.",
      },
      cards: [
        {
          title: "Challenges",
          text: "A monthly session goal with a reward at the finish.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Session milestones",
          text: "Recognise the 10th, 50th and 100th session.",
          ui: {
            kind: "goal",
            label: "Monthly goal",
            value: "₹7,200",
            of: "of ₹10,000",
            progress: 72,
            note: "₹2,800 to go",
          },
        },
        {
          title: "Refer-a-friend",
          text: "Reward members whose friends join.",
          ui: {
            kind: "segments",
            items: [
              {
                label: "New",
                segment: "new",
              },
              {
                label: "Repeat",
                segment: "repeat",
              },
              {
                label: "VIP",
                segment: "vip",
              },
              {
                label: "At risk",
                segment: "atRisk",
              },
              {
                label: "Lost",
                segment: "lost",
              },
            ],
          },
        },
        {
          title: "Trial-to-member offer",
          text: "A welcome reward for trial customers who sign up.",
          ui: {
            kind: "ticket",
            label: "Reward code",
            code: "FYD-4K9",
            note: "Show it at the counter",
          },
        },
        {
          title: "Check-in messages",
          text: "A message when a member has been away for a while.",
          ui: {
            kind: "bubble",
            message: "We miss you! Here is something for your next visit.",
            time: "11:12 am ✓✓",
          },
        },
        {
          title: "Top member recognition",
          text: "Single out your most committed members.",
          ui: {
            kind: "vip",
            title: "VIP",
            note: "Early access message",
          },
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try a monthly challenge",
        intro: "Add sessions and watch the challenge reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 20,
        unit: "sessions",
        title: "Complete 20 sessions this month",
        addLabel: "Add a session",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "A studio T-shirt",
          note: "Awarded when the monthly challenge is complete.",
        },
      },
    },
    {
      kind: "message",
      head: {
        tone: "cream",
        id: "message",
        heading: "A message customers will read",
        intro: "Keep members moving towards the finish line.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Fitness Studio",
        subtitle: "Business account",
      },
      message: {
        text: "Hi Karan, you are on session 14 of 20 in this month's challenge. Six more to earn your studio T-shirt!",
        card: {
          title: "Challenge reward",
          note: "finish 20 sessions",
        },
        button: "See my progress",
        time: "10:42 am",
      },
      panel: {
        title: "A message that keeps the challenge alive",
        text: "Keep members moving towards the finish line.",
        points: [
          "Sent partway through a challenge",
          "Shows progress and the reward",
          "Encourages one more session",
        ],
      },
    },
    {
      kind: "journey",
      head: {
        tone: "tint",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "From trial to regular",
          steps: [
            {
              icon: Dumbbell,
              title: "Trial session",
              text: "A new customer tries a class and is invited to join.",
            },
            {
              icon: Trophy,
              title: "First challenge",
              text: "A welcome challenge builds the routine in the first month.",
            },
            {
              icon: UserPlus,
              title: "Bring a friend",
              text: "A happy member brings a friend and both are rewarded.",
            },
          ],
        },
      ],
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "Can I run a monthly challenge?",
          answer: "Yes. Set a session goal and a reward, and members earn it when they reach the goal.",
        },
        {
          question: "Can I turn trials into members?",
          answer: "Yes. Send trial customers a welcome offer for signing up.",
        },
        {
          question: "Can members refer friends?",
          answer: "Yes. You can reward members whose friends join.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Gyms",
          route: "/loyalty-program-for-gyms",
          description: "Support consistency and renewals.",
        },
        {
          label: "Yoga Studios",
          route: "/loyalty-program-for-yoga-studios",
          description: "Reward practice, class packs and community.",
        },
        {
          label: "Referral rewards",
          route: "/referral-rewards-software",
          description: "Reward customers who bring their friends.",
        },
        {
          label: "Milestones",
          route: "/tiered-loyalty-program#milestones",
          description: "Rewards when customers reach goals you define.",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about Fitness Studios",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const ecommerce = {
  seo: {
    path: "/loyalty-program-for-ecommerce",
    title: "Loyalty Program for E-commerce Stores (B2C) | Fydo",
    description: "A loyalty program for e-commerce stores: second-order rewards, post-delivery WhatsApp messages and cashback that turn first-time buyers into repeat buyers.",
  },
  breadcrumb: "E-commerce B2C",
  accent: "sky",
  hero: {
    title: "Turn first-time online buyers into repeat customers",
    lead: "Online shoppers compare prices in seconds. Give them a reason to come back to your store: a reward on the next order, a message after delivery, and one profile for every customer.",
    ctas: heroCtas,
    scene: "ecommerce",
    tone: "blush",
    alt: "E-commerce B2C illustration",
    chips: [
      {
        text: "**+₹100** on your next order",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Your order is delivered 📦",
        variant: "whatsapp",
        x: 50,
        y: 93,
      },
      {
        text: "2nd order reward",
        variant: "ink",
        x: 64,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "Where online stores lose customers",
        intro: "The first order is the expensive one. The second is where it pays back.",
      },
      cards: [
        {
          title: "Acquisition costs a lot",
          text: "Most of the budget goes into winning a first order. Without a second order, it rarely pays back.",
        },
        {
          title: "Discounts teach shoppers to wait",
          text: "Blanket sales cut margin and train customers to buy only on offer.",
        },
        {
          title: "Customers vanish after delivery",
          text: "Once the parcel arrives, many shoppers never hear from the store again.",
        },
        {
          title: "Returns and cash on delivery add drag",
          text: "Rewards that follow a completed order keep the focus on orders that stick.",
        },
      ],
    },
    {
      kind: "mechanisms",
      head: {
        tone: "tint",
        id: "mechanisms",
        heading: "What Fydo can do for your store",
        intro: "Rewards and messages built around the order cycle.",
      },
      cards: [
        {
          title: "Second-order reward",
          text: "A reward that unlocks on the next order, not the first.",
          ui: {
            kind: "stamps",
            filled: 3,
            total: 5,
            note: "3 of 5 visits",
            title: "Free dessert next",
          },
        },
        {
          title: "Cashback balance",
          text: "Return a share of each order as balance for the next one.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Post-delivery WhatsApp",
          text: "A thank-you, a review request and an offer after delivery.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Refer-a-friend",
          text: "Reward shoppers who bring a friend to your store.",
          ui: {
            kind: "segments",
            items: [
              {
                label: "New",
                segment: "new",
              },
              {
                label: "Repeat",
                segment: "repeat",
              },
              {
                label: "VIP",
                segment: "vip",
              },
              {
                label: "At risk",
                segment: "atRisk",
              },
              {
                label: "Lost",
                segment: "lost",
              },
            ],
          },
        },
        {
          title: "Reorder reminders",
          text: "A nudge when a regular product is likely to run out.",
          ui: {
            kind: "bubble",
            message: "We miss you! Here is something for your next visit.",
            time: "11:12 am ✓✓",
          },
        },
        {
          title: "Early access",
          text: "Invite top spenders to new collections first.",
          ui: {
            kind: "vip",
            title: "VIP",
            note: "Early access message",
          },
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try a second-order reward",
        intro: "Add orders and watch the reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 3,
        unit: "orders",
        title: "Every 3rd order earns a reward",
        addLabel: "Place an order",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "₹150 off the next order",
          note: "Valid on the customer's next order.",
        },
      },
    },
    {
      kind: "journey",
      head: {
        tone: "cream",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "From first order to reorder",
          steps: [
            {
              icon: ShoppingBag,
              title: "First order",
              text: "A customer buys from your store for the first time.",
            },
            {
              icon: Package,
              title: "After delivery",
              text: "A WhatsApp message arrives with a thank-you and a reward.",
            },
            {
              icon: Repeat,
              title: "Second order",
              text: "The reward brings them back to order again.",
            },
          ],
        },
      ],
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Reach shoppers when they are most likely to reorder.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Nest",
        subtitle: "Business account",
      },
      message: {
        text: "Hi Priya, your order has been delivered. Thank you! Here is ₹100 off your next order, valid for 15 days.",
        card: {
          title: "₹100 off",
          note: "on your next order",
        },
        button: "Shop now",
        time: "10:42 am",
      },
      panel: {
        title: "A message that follows the parcel",
        text: "Reach shoppers when they are most likely to reorder.",
        points: [
          "Sent after delivery to every customer, or only to first-time buyers",
          "Carries a reward that expires, so the next order comes sooner",
          "Reaches customers on the app they already use",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "How do customers earn rewards?",
          answer: "You set the rule, for example a reward on the second order, or cashback on every order.",
        },
        {
          question: "Do I have to discount for everyone?",
          answer: "No. Rewards can be tied to the next order, a spend goal or a chosen customer group.",
        },
        {
          question: "How do customers hear about their rewards?",
          answer: "Through WhatsApp messages and campaigns you send to the customers you choose.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Cashback",
          route: "/cashback-loyalty-program",
          description: "Return a share of each bill as money back on a later purchase.",
        },
        {
          label: "Referral rewards",
          route: "/referral-rewards-software",
          description: "Reward customers who bring their friends.",
        },
        {
          label: "WhatsApp Marketing",
          route: "/whatsapp-loyalty-marketing",
          description: "Campaigns, offers and notifications on WhatsApp.",
        },
        {
          label: "E-commerce B2B",
          route: "/loyalty-program-for-b2b-ecommerce",
          description: "Reward reorders from business buyers.",
        },
        {
          label: "Guides",
          route: "/guides",
          description: "Practical guides to planning a loyalty program.",
        },
        {
          label: "E-commerce & D2C",
          route: "/solutions/ecommerce-d2c",
          description: "Loyalty solutions for e-commerce and direct-to-consumer brands: reward repeat orders, win",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about E-commerce B2C",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const b2bEcommerce = {
  seo: {
    path: "/loyalty-program-for-b2b-ecommerce",
    title: "Loyalty Program for B2B E-commerce | Fydo",
    description: "A loyalty program for B2B e-commerce: volume goals, account-level rewards and timely reorder messages that keep business buyers ordering again.",
  },
  breadcrumb: "E-commerce B2B",
  accent: "mint",
  hero: {
    title: "Make reordering the easy choice for business buyers",
    lead: "Business buyers reorder on a rhythm and buy in volume. Recognise every account, reward the volume they bring, and remind them before they run low.",
    ctas: heroCtas,
    scene: "b2bEcommerce",
    tone: "mint",
    alt: "E-commerce B2B illustration",
    chips: [
      {
        text: "Quarterly goal **80%**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Time to reorder? Stock is low.",
        variant: "whatsapp",
        x: 46,
        y: 93,
      },
      {
        text: "Volume reward unlocked",
        variant: "ink",
        x: 60,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "What B2B sellers deal with",
        intro: "Fewer customers, bigger orders, and a lot riding on each account.",
      },
      cards: [
        {
          title: "Orders are large and few",
          text: "Losing one account can cost a full month of sales.",
        },
        {
          title: "Reordering depends on a rhythm",
          text: "Buyers reorder on a cycle. A missed reminder can mean a missed order.",
        },
        {
          title: "Buyers compare on price and service",
          text: "Recognition and fair rewards for volume help an account stay with you.",
        },
        {
          title: "Many people sit behind one account",
          text: "Owners, purchasers and accountants each touch the order. Each one should hear the right message.",
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try a volume goal",
        intro: "Add orders and watch the account reach its goal.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 5,
        unit: "orders",
        title: "Quarterly volume goal: 5 orders",
        addLabel: "Add an order",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "A 3% rebate on the next order",
          note: "Credited once the quarterly goal is met.",
        },
      },
    },
    {
      kind: "mechanisms",
      head: {
        tone: "white",
        id: "mechanisms",
        heading: "What Fydo can do for your accounts",
        intro: "Account-aware rewards, built for volume.",
      },
      cards: [
        {
          title: "Volume goals",
          text: "A reward when an account reaches its monthly or quarterly target.",
          ui: {
            kind: "goal",
            label: "Monthly goal",
            value: "₹7,200",
            of: "of ₹10,000",
            progress: 72,
            note: "₹2,800 to go",
          },
        },
        {
          title: "Milestones",
          text: "Recognise an account's 10th order or first year.",
          ui: {
            kind: "goal",
            label: "Monthly goal",
            value: "₹7,200",
            of: "of ₹10,000",
            progress: 72,
            note: "₹2,800 to go",
          },
        },
        {
          title: "Reorder reminders",
          text: "A message when an account is due to order again.",
          ui: {
            kind: "bubble",
            message: "We miss you! Here is something for your next visit.",
            time: "11:12 am ✓✓",
          },
        },
        {
          title: "Rebate balance",
          text: "Return a share of volume as credit on the next order.",
          ui: {
            kind: "goal",
            label: "Monthly goal",
            value: "₹7,200",
            of: "of ₹10,000",
            progress: 72,
            note: "₹2,800 to go",
          },
        },
        {
          title: "Top account recognition",
          text: "Single out your best accounts for early access and priority.",
          ui: {
            kind: "vip",
            title: "VIP",
            note: "Early access message",
          },
        },
        {
          title: "Account view",
          text: "See each account's orders and spend in one customer record.",
          ui: {
            kind: "points",
            label: "Rate",
            value: "1 point",
            note: "for every ₹100",
          },
        },
      ],
    },
    {
      kind: "message",
      head: {
        tone: "cream",
        id: "message",
        heading: "A message customers will read",
        intro: "Keep accounts ordering on time.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Traders",
        subtitle: "Business account",
      },
      message: {
        text: "Hello Mr Shah, your monthly order is due. Reorder this week and add to your quarterly volume goal.",
        card: {
          title: "Time to reorder",
          note: "Your usual order is ready",
        },
        button: "Reorder now",
        time: "10:42 am",
      },
      panel: {
        title: "A reminder at the right moment",
        text: "Keep accounts ordering on time.",
        points: [
          "Sent to accounts that are due to reorder",
          "Shows progress towards the account's goal",
          "Goes to the person who places the orders",
        ],
      },
    },
    {
      kind: "journey",
      head: {
        tone: "tint",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "From first order to a loyal account",
          steps: [
            {
              icon: ClipboardList,
              title: "First order",
              text: "A business places its first order.",
            },
            {
              icon: Target,
              title: "Volume goal",
              text: "The account sees a goal and a reward for reaching it.",
            },
            {
              icon: RotateCcw,
              title: "Reorder",
              text: "A reminder arrives before stock runs low, and the account reorders.",
            },
          ],
        },
      ],
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "Can rewards be set per account?",
          answer: "Yes. Rewards and goals can be set for a chosen group of customers, so large accounts get what suits them.",
        },
        {
          question: "Is this only for online orders?",
          answer: "No. Any order recorded for a customer can count towards their goal.",
        },
        {
          question: "How do we remind accounts to reorder?",
          answer: "With WhatsApp campaigns sent to the accounts you choose, at the time you choose.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "E-commerce B2C",
          route: "/loyalty-program-for-ecommerce",
          description: "Reward repeat orders from online shoppers.",
        },
        {
          label: "Milestones",
          route: "/tiered-loyalty-program#milestones",
          description: "Rewards when customers reach goals you define.",
        },
        {
          label: "WhatsApp Marketing",
          route: "/whatsapp-loyalty-marketing",
          description: "Campaigns, offers and notifications on WhatsApp.",
        },
        {
          label: "Customer Intelligence",
          route: "/products/customer-intelligence",
          description: "A customer intelligence platform for shops: build customer profiles, group customers, see",
        },
        {
          label: "Guides",
          route: "/guides",
          description: "Practical guides to planning a loyalty program.",
        },
        {
          label: "E-commerce & D2C",
          route: "/solutions/ecommerce-d2c",
          description: "Loyalty solutions for e-commerce and direct-to-consumer brands: reward repeat orders, win",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about E-commerce B2B",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const manufacturing = {
  seo: {
    path: "/loyalty-program-for-manufacturing",
    title: "Loyalty Program for Manufacturers | Fydo",
    description: "Reach the people who use your products with in-pack QR rewards, repeat-purchase rewards and recognition for channel partners.",
  },
  breadcrumb: "Manufacturing",
  accent: "sun",
  hero: {
    title: "Know the people who use what you make",
    lead: "Manufacturers sell through distributors and dealers, so the buyer stays anonymous. A QR code in the pack gives every buyer a reason to scan, and gives you a direct line to them.",
    ctas: heroCtas,
    scene: "manufacturing",
    tone: "blush",
    alt: "Manufacturing illustration",
    chips: [
      {
        text: "Scan the pack, **earn ₹50**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Thanks for scanning! Your reward is in.",
        variant: "whatsapp",
        x: 40,
        y: 93,
      },
      {
        text: "Dealer milestone reached",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "Where manufacturers lose sight of the buyer",
        intro: "The product leaves the factory. The customer relationship rarely follows.",
      },
      cards: [
        {
          title: "The buyer is anonymous",
          text: "Distributors and dealers hold the relationship, so you rarely know who uses your product.",
        },
        {
          title: "Repeat purchase is invisible",
          text: "You cannot see who buys again, or who has switched.",
        },
        {
          title: "Channel partners decide the sale",
          text: "Dealers, mechanics and installers influence the choice, and often get no recognition.",
        },
        {
          title: "Promotions are blunt",
          text: "Trade offers reach the channel, not the end buyer.",
        },
      ],
    },
    {
      kind: "journey",
      head: {
        tone: "tint",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "The buyer's journey",
          steps: [
            {
              icon: PackageOpen,
              title: "Open the pack",
              text: "The buyer finds a QR code inside the pack.",
            },
            {
              icon: QrCode,
              title: "Scan and register",
              text: "They scan, share their phone number and earn cashback.",
            },
            {
              icon: Repeat,
              title: "Buy again",
              text: "A message and a bonus reward bring them back for the next pack.",
            },
          ],
        },
        {
          tag: "The channel partner's journey",
          steps: [
            {
              icon: Handshake,
              title: "Make the sale",
              text: "A dealer or mechanic recommends the product.",
            },
            {
              icon: Target,
              title: "Reach a milestone",
              text: "Sales linked to the partner build towards a reward.",
            },
            {
              icon: Award,
              title: "Get recognised",
              text: "The partner receives a reward and a thank-you message.",
            },
          ],
        },
      ],
    },
    {
      kind: "mechanisms",
      head: {
        tone: "white",
        id: "mechanisms",
        heading: "What Fydo can do for a manufacturer",
        intro: "Reward the buyer and the channel, and learn who they are.",
      },
      cards: [
        {
          title: "In-pack QR rewards",
          text: "A code inside the pack that the buyer scans to earn cashback.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Cashback to the buyer",
          text: "Credit the reward to the customer's balance on scan.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Repeat-purchase rewards",
          text: "Reward the buyer who comes back for the next pack.",
          ui: {
            kind: "stamps",
            filled: 3,
            total: 5,
            note: "3 of 5 visits",
            title: "Free dessert next",
          },
        },
        {
          title: "Channel partner rewards",
          text: "Recognise dealers, mechanics and installers who bring sales.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Direct WhatsApp",
          text: "Reach buyers who scanned, with offers and reminders.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "A customer record",
          text: "See who scans, how often and where the interest is.",
          ui: {
            kind: "points",
            label: "Rate",
            value: "1 point",
            note: "for every ₹100",
          },
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try a pack-scan reward",
        intro: "Scan packs and watch the buyer's reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 5,
        unit: "packs scanned",
        title: "Scan 5 packs, earn a bonus",
        addLabel: "Scan a pack",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "₹100 bonus cashback",
          note: "Credited to the buyer after the fifth scan.",
        },
      },
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Turn a scan into a conversation.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Industries",
        subtitle: "Business account",
      },
      message: {
        text: "Thank you for scanning your pack, Ramesh! ₹50 cashback has been added to your wallet. Scan your next pack to earn more.",
        card: {
          title: "₹50 cashback",
          note: "for scanning your pack",
        },
        button: "View my wallet",
        time: "10:42 am",
      },
      panel: {
        title: "A thank-you that starts the relationship",
        text: "Turn a scan into a conversation.",
        points: [
          "Sent the moment a buyer scans",
          "Shows the reward earned",
          "Invites the buyer to scan the next pack",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "How does an in-pack QR reward work?",
          answer: "The buyer scans the code inside the pack, shares their phone number and earns a reward you set.",
        },
        {
          question: "Can dealers and mechanics be rewarded too?",
          answer: "Yes. Channel partners can have their own goals and rewards.",
        },
        {
          question: "Do I need to change my distribution?",
          answer: "No. Your products still sell through the same channel. The QR code adds a direct link to the buyer.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "FMCG & Consumer Brands",
          route: "/loyalty-program-for-consumer-brands",
          description: "Connect packaged goods with repeat buyers.",
        },
        {
          label: "QR loyalty and earning rules",
          route: "/qr-code-loyalty-program",
          description: "Join with a scan, then earn by visits, spend or occasions.",
        },
        {
          label: "Cashback",
          route: "/cashback-loyalty-program",
          description: "Return a share of each bill as money back on a later purchase.",
        },
        {
          label: "WhatsApp Marketing",
          route: "/whatsapp-loyalty-marketing",
          description: "Campaigns, offers and notifications on WhatsApp.",
        },
        {
          label: "Consumer Brands & Manufacturing",
          route: "/solutions/consumer-brands-manufacturing",
          description: "Loyalty solutions for consumer brands and manufacturers: reach the people who use your pro",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about Manufacturing",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const consumerBrands = {
  seo: {
    path: "/loyalty-program-for-consumer-brands",
    title: "Loyalty Program for FMCG and Consumer Brands | Fydo",
    description: "Connect packaged goods with repeat buyers through scan-to-earn packs, repeat-purchase rewards and share-and-earn offers for consumer brands.",
  },
  breadcrumb: "FMCG & Consumer Brands",
  accent: "mint",
  hero: {
    title: "Turn every pack into a direct link to your customer",
    lead: "Fast-moving brands sell in huge volumes through shops they do not own. Scan-to-earn packs let you see who buys, reward the habit, and reach them directly.",
    ctas: heroCtas,
    scene: "consumerBrands",
    tone: "mint",
    alt: "FMCG & Consumer Brands illustration",
    chips: [
      {
        text: "Scan to earn **₹20**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Your favourite is back in stock! 🛒",
        variant: "whatsapp",
        x: 44,
        y: 93,
      },
      {
        text: "Repeat buyer reward",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "What consumer brands face",
        intro: "High volume, low margin, and almost no direct contact.",
      },
      cards: [
        {
          title: "Shelf competition is constant",
          text: "Shoppers switch brands for a small offer, and you rarely find out why.",
        },
        {
          title: "You do not know your buyers",
          text: "Sales data stops at the retailer, so loyal buyers look like everyone else.",
        },
        {
          title: "Trade discounts do not reach the shopper",
          text: "Offers go to the channel, not to the person who decides at the shelf.",
        },
        {
          title: "Launches need a way to reach people",
          text: "A new pack needs a direct way to tell existing buyers.",
        },
      ],
    },
    {
      kind: "mechanisms",
      head: {
        tone: "tint",
        id: "mechanisms",
        heading: "What Fydo can do for a brand",
        intro: "Reward the habit and reach buyers directly.",
      },
      cards: [
        {
          title: "Scan-to-earn packs",
          text: "A code on the pack that rewards the buyer for scanning.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Repeat-buyer rewards",
          text: "A reward for the buyer who scans pack after pack.",
          ui: {
            kind: "stamps",
            filled: 3,
            total: 5,
            note: "3 of 5 visits",
            title: "Free dessert next",
          },
        },
        {
          title: "Share-and-earn",
          text: "Reward buyers who bring a friend to the brand.",
          ui: {
            kind: "segments",
            items: [
              {
                label: "New",
                segment: "new",
              },
              {
                label: "Repeat",
                segment: "repeat",
              },
              {
                label: "VIP",
                segment: "vip",
              },
              {
                label: "At risk",
                segment: "atRisk",
              },
              {
                label: "Lost",
                segment: "lost",
              },
            ],
          },
        },
        {
          title: "Launch campaigns",
          text: "Tell scanners about a new pack the day it arrives.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Festival offers",
          text: "Seasonal rewards for scanners ahead of festivals.",
          ui: {
            kind: "banner",
            kicker: "This week",
            title: "Festival reward",
          },
        },
        {
          title: "Buyer insight",
          text: "See scan activity and repeat behaviour by pack.",
          ui: {
            kind: "points",
            label: "Rate",
            value: "1 point",
            note: "for every ₹100",
          },
        },
      ],
    },
    {
      kind: "journey",
      head: {
        tone: "white",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "From shelf to repeat buyer",
          steps: [
            {
              icon: ShoppingBasket,
              title: "Pick up the pack",
              text: "A shopper chooses your product at the shop.",
            },
            {
              icon: QrCode,
              title: "Scan to earn",
              text: "They scan the pack and earn a small reward.",
            },
            {
              icon: MessageCircle,
              title: "Hear from the brand",
              text: "A message brings them back for the next pack.",
            },
          ],
        },
      ],
    },
    {
      kind: "goal",
      head: {
        tone: "cream",
        id: "try",
        heading: "Try scan-to-earn",
        intro: "Scan packs and watch the repeat-buyer reward unlock.",
      },
      tracker: {
        ariaLabel: "An interactive progress tracker: add steps until the reward unlocks.",
        goal: 4,
        unit: "packs scanned",
        title: "Every 4th pack earns a reward",
        addLabel: "Scan a pack",
        resetLabel: "Start again",
        reward: {
          kicker: "Reward unlocked",
          title: "A free pack voucher",
          note: "Delivered by message after the fourth scan.",
        },
      },
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Tell buyers about new packs and offers.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Foods",
        subtitle: "Business account",
      },
      message: {
        text: "Thanks for scanning, Kavita! ₹20 has been added to your balance. Our new pack is in stores now, scan it to earn more.",
        card: {
          title: "₹20 earned",
          note: "on your pack scan",
        },
        button: "See rewards",
        time: "10:42 am",
      },
      panel: {
        title: "A message that follows the scan",
        text: "Tell buyers about new packs and offers.",
        points: [
          "Sent to people who scanned the pack",
          "Announces a launch or a festival pack",
          "Invites a repeat scan",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "How is a scan connected to a reward?",
          answer: "The buyer scans the pack, shares their phone number and earns the reward you set for that pack.",
        },
        {
          question: "Can I reward repeat purchases?",
          answer: "Yes. You can set a reward for a number of scans, such as every fourth pack.",
        },
        {
          question: "Will this change how I sell to retailers?",
          answer: "No. Your retail channel stays the same. The scan adds a direct link to the shopper.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Manufacturing",
          route: "/loyalty-program-for-manufacturing",
          description: "Reach buyers beyond the distributor.",
        },
        {
          label: "QR loyalty and earning rules",
          route: "/qr-code-loyalty-program",
          description: "Join with a scan, then earn by visits, spend or occasions.",
        },
        {
          label: "Cashback",
          route: "/cashback-loyalty-program",
          description: "Return a share of each bill as money back on a later purchase.",
        },
        {
          label: "Marketing Campaigns",
          route: "/products/marketing-campaigns",
          description: "Run customer retention campaigns for your shop: promotional offers, win-back messages and",
        },
        {
          label: "Consumer Brands & Manufacturing",
          route: "/solutions/consumer-brands-manufacturing",
          description: "Loyalty solutions for consumer brands and manufacturers: reach the people who use your pro",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about FMCG & Consumer Brands",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;
const enterprise = {
  seo: {
    path: "/loyalty-program-for-enterprise",
    title: "Loyalty Program for Enterprise Businesses | Fydo",
    description: "Run one loyalty programme across brands, regions and teams, with consistent rules, clear roles and reporting leadership can trust.",
  },
  breadcrumb: "Enterprise",
  accent: "lilac",
  hero: {
    title: "Loyalty at scale, with control and consistency",
    lead: "Large businesses need one programme that works across brands, regions and teams. Set the rules centrally, let each team run its own campaigns, and see the whole picture in one place.",
    ctas: heroCtas,
    scene: "enterprise",
    tone: "sky",
    alt: "Enterprise illustration",
    chips: [
      {
        text: "All brands, **one view**",
        variant: "plain",
        x: -4,
        y: -7,
      },
      {
        text: "Regional offer approved ✓",
        variant: "whatsapp",
        x: 46,
        y: 93,
      },
      {
        text: "Head office rules",
        variant: "ink",
        x: 62,
        y: -9,
      },
    ],
  },
  sections: [
    {
      kind: "challenge",
      head: {
        tone: "cream",
        id: "challenge",
        heading: "What large businesses need",
        intro: "Scale makes consistency hard and visibility harder.",
      },
      cards: [
        {
          title: "Rules drift across teams",
          text: "Different regions and brands run different offers, and customers notice.",
        },
        {
          title: "Leadership lacks one view",
          text: "Results sit in separate reports, so it is hard to see what works.",
        },
        {
          title: "Many people need access",
          text: "Head office, regional managers and store teams each need the right level of access.",
        },
        {
          title: "Campaigns need oversight",
          text: "Offers go out to many customers, so they need clear ownership and review.",
        },
      ],
    },
    {
      kind: "org",
      head: {
        tone: "cream",
        id: "structure",
        heading: "One structure, from head office to every outlet",
        intro: "Rules and reporting flow through the levels of your organisation.",
      },
      chart: {
        ariaLabel: "An organisation chart: head office, groups and outlets.",
        top: {
          title: "Head office",
          text: "Sets programme rules and reads the full picture",
        },
        groups: [
          {
            title: "Brand A",
            text: "Own offers, shared rules",
          },
          {
            title: "Brand B",
            text: "Own offers, shared rules",
          },
          {
            title: "Brand C",
            text: "Own offers, shared rules",
          },
        ],
        outlets: [
          "Outlet 1",
          "Outlet 2",
          "Outlet 3",
          "Outlet 4",
          "Outlet 5",
          "Outlet 6",
        ],
      },
    },
    {
      kind: "mechanisms",
      head: {
        tone: "white",
        id: "mechanisms",
        heading: "What Fydo can do for an enterprise",
        intro: "One programme, many teams.",
      },
      cards: [
        {
          title: "Central programme rules",
          text: "Set the core rewards once for every brand and outlet.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Team access",
          text: "Give head office, regions and stores the access they need.",
          ui: {
            kind: "segments",
            items: [
              {
                label: "New",
                segment: "new",
              },
              {
                label: "Repeat",
                segment: "repeat",
              },
              {
                label: "VIP",
                segment: "vip",
              },
              {
                label: "At risk",
                segment: "atRisk",
              },
              {
                label: "Lost",
                segment: "lost",
              },
            ],
          },
        },
        {
          title: "Campaigns by team",
          text: "Let regions and brands run campaigns within the rules.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Reporting across brands",
          text: "See customers, spend and campaigns across the business.",
          ui: {
            kind: "points",
            label: "Rate",
            value: "1 point",
            note: "for every ₹100",
          },
        },
        {
          title: "Location and region views",
          text: "Compare outlets and regions side by side.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
        {
          title: "Tailored to your structure",
          text: "Shape the programme around how your organisation works.",
          ui: {
            kind: "cash",
            label: "Cashback added",
            value: "+₹24",
            progress: 48,
            note: "Wallet: ₹104",
          },
        },
      ],
    },
    {
      kind: "journey",
      head: {
        tone: "cream",
        id: "journey",
        heading: "The customer journey",
        intro: "How it feels from the customer's side.",
      },
      ariaLabel: "The customer journey, step by step.",
      lanes: [
        {
          tag: "How a programme rolls out",
          steps: [
            {
              icon: Settings2,
              title: "Define the rules",
              text: "Head office agrees the rewards and how customers earn them.",
            },
            {
              icon: KeyRound,
              title: "Give teams access",
              text: "Brands, regions and stores get the access they need.",
            },
            {
              icon: ChartColumn,
              title: "Review the results",
              text: "Leadership reads customers, spend and campaign results together.",
            },
          ],
        },
      ],
    },
    {
      kind: "message",
      head: {
        tone: "tint",
        id: "message",
        heading: "A message customers will read",
        intro: "Customers hear a consistent message, wherever they shop.",
      },
      chat: {
        initial: "F",
        shop: "Fydo Group",
        subtitle: "Business account",
      },
      message: {
        text: "Dear Anil, thank you for shopping with the Fydo Group. Your rewards work at all our brands and locations.",
        card: {
          title: "Thank you, Anil",
          note: "for being a valued customer",
        },
        button: "View rewards",
        time: "10:42 am",
      },
      panel: {
        title: "One voice across every brand",
        text: "Customers hear a consistent message, wherever they shop.",
        points: [
          "Written once and sent to the customers you choose",
          "Keeps the tone consistent across brands",
          "Can be approved before it goes out",
        ],
      },
    },
    {
      kind: "faq",
      head: {
        tone: "white",
        heading: "Common questions",
      },
      items: [
        {
          question: "Can each brand run its own offers?",
          answer: "Yes. Brands and regions can run campaigns while the core programme rules stay consistent.",
        },
        {
          question: "Who can see the reports?",
          answer: "Access can be given by role, so leadership sees the full picture and teams see their own.",
        },
        {
          question: "Can the programme be tailored to our structure?",
          answer: "Yes. Talk to us about how your organisation works and we will shape the programme around it.",
        },
      ],
    },
    {
      kind: "related",
      head: {
        tone: "cream",
        heading: "Related",
      },
      cards: [
        {
          label: "Multi-location and franchise",
          route: "/loyalty-program-for-multi-location-businesses",
          description: "One loyalty program across outlets and franchisees.",
        },
        {
          label: "Franchise Businesses",
          route: "/solutions/franchise-loyalty",
          description: "Shared loyalty across franchise outlets.",
        },
        {
          label: "Tailored Loyalty",
          route: "/tailored-loyalty-solutions",
          description: "Your business is unique. Request a loyalty experience built around it.",
        },
        {
          label: "Customer Intelligence",
          route: "/products/customer-intelligence",
          description: "A customer intelligence platform for shops: build customer profiles, group customers, see",
        },
        {
          label: "Loyalty Programs",
          route: "/products/loyalty-programs",
          description: "Loyalty program software for shops: choose cashback, points, rewards, QR loyalty and refer",
        },
        {
          label: "Marketing Campaigns",
          route: "/products/marketing-campaigns",
          description: "Run customer retention campaigns for your shop: promotional offers, win-back messages and",
        },
        {
          label: "Enterprise & Multi-Location",
          route: "/solutions/enterprise-multi-location",
          description: "Loyalty solutions for enterprises and multi-location businesses: one program across outlet",
        },
      ],
    },
  ],
  finalCta: {
    title: "Talk to us about Enterprise",
    text: "Tell us how your business works and we will show you how Fydo fits.",
    ...finalCtaLinks,
  },
} satisfies IndustryPageData;

export const industryPages = {
  "loyalty-program-for-spas": spas,
  "loyalty-program-for-gyms": gyms,
  "loyalty-program-for-yoga-studios": yogaStudios,
  "loyalty-program-for-fitness-studios": fitnessStudios,
  "loyalty-program-for-ecommerce": ecommerce,
  "loyalty-program-for-b2b-ecommerce": b2bEcommerce,
  "loyalty-program-for-manufacturing": manufacturing,
  "loyalty-program-for-consumer-brands": consumerBrands,
  "loyalty-program-for-enterprise": enterprise,
};

export type IndustrySlug = keyof typeof industryPages;
